// src/email-domain.ts
var FREEMAIL_DOMAINS = /* @__PURE__ */ new Set([
  "gmail.com",
  "googlemail.com",
  "outlook.com",
  "hotmail.com",
  "hotmail.ch",
  "live.com",
  "live.ch",
  "msn.com",
  "yahoo.com",
  "yahoo.de",
  "ymail.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "proton.me",
  "protonmail.com",
  // Schweiz
  "gmx.ch",
  "gmx.net",
  "gmx.de",
  "gmx.at",
  "bluewin.ch",
  "hispeed.ch",
  "sunrise.ch",
  "green.ch",
  "sensemail.ch",
  "vtxmail.ch",
  "quickline.ch",
  "bluemail.ch",
  "swissonline.ch",
  "centralnet.ch",
  "freesurf.ch"
]);
var DISPOSABLE_DOMAINS = /* @__PURE__ */ new Set([
  "10minutemail.com",
  "10minutemail.net",
  "20minutemail.com",
  "anonbox.net",
  "burnermail.io",
  "dispostable.com",
  "dropmail.me",
  "emailfake.com",
  "emailondeck.com",
  "fakeinbox.com",
  "getnada.com",
  "guerrillamail.com",
  "guerrillamail.net",
  "guerrillamail.org",
  "harakirimail.com",
  "mailcatch.com",
  "maildrop.cc",
  "mailforspam.com",
  "mailinator.com",
  "mailinator.net",
  "moakt.com",
  "mohmal.com",
  "sharklasers.com",
  "spam4.me",
  "temp-mail.org",
  "tempmail.com",
  "tempmailo.com",
  "throwawaymail.com",
  "trashmail.com",
  "trashmail.de",
  "yopmail.com",
  "yopmail.net",
  "mailnesia.com",
  "tempr.email"
]);
var EMAIL_RE = /^[^\s@]+@([^\s@]+\.[^\s@]+)$/;
function emailDomainOf(email) {
  const m = (email ?? "").trim().toLowerCase().match(EMAIL_RE);
  return m?.[1] ?? null;
}
function isBlockedEmailDomain(domain) {
  if (!domain) return true;
  const d = domain.trim().toLowerCase();
  return FREEMAIL_DOMAINS.has(d) || DISPOSABLE_DOMAINS.has(d);
}
function isCompanyEmail(email) {
  const d = emailDomainOf(email);
  return d !== null && !isBlockedEmailDomain(d);
}
function emailMatchesCompanyDomain(email, companyDomain) {
  const d = emailDomainOf(email);
  return d !== null && d === companyDomain.trim().toLowerCase();
}

// src/enums.ts
var ROLES = ["ADMIN", "GUTACHTER", "MITARBEITER", "LESER"];
var CONTRACT_TYPES = ["WERKVERTRAG", "GRUNDSTUECKKAUF", "PLANERVERTRAG", "GEMISCHT"];
var LEGAL_REGIMES = ["CH_OR_PRE_2026", "CH_OR_2026"];
var REGIME_CUTOFF_ISO = "2026-01-01";
var SIA118_OPTIONS = ["JA", "NEIN", "UNKLAR"];
var CLAIM_TYPES = [
  "MINDERUNG",
  "SCHADENERSATZ",
  "MERKANTILER_MINDERWERT",
  "NUTZUNGSDAUERVERKUERZUNG",
  "WIRTSCHAFTLICHER_VERGLEICH"
];
var CLAIM_STATUSES = [
  "ANSPRUCHSGRUNDLAGE_VORGEPRUEFT",
  "VORAUSSETZUNGEN_TEILWEISE_OFFEN",
  "NUR_WIRTSCHAFTLICHES_VERGLEICHSSZENARIO",
  "ANSPRUCH_MOEGLICHERWEISE_AUSGESCHLOSSEN",
  "RECHTLICHE_PRUEFUNG_ERFORDERLICH",
  "NICHT_ALS_ANSPRUCHSBETRAG_VERWENDBAR"
];
var EVIDENCE_LEVELS = ["A", "B", "C", "D", "E"];
var SCENARIO_RELATIONS = ["ALTERNATIV", "KUMULATIV", "SUBSIDIAER", "UNGEKLAERT"];
var SOURCE_TYPES = [
  "RECHTSPRECHUNG",
  "NORM",
  "LITERATUR",
  "MARKTDATEN",
  "BEWERTUNG",
  "SONSTIGE"
];
var REVIEW_STATUSES = ["AUSSTEHEND", "FREIGEGEBEN", "ABGELEHNT"];
var MAERKTE = ["CH", "DE"];
var DATENKLASSEN = ["DEMO", "SYNTHETIC", "REAL_CURATED", "PUBLIC_DECISION"];
var NICHT_EVIDENZ_KLASSEN = ["DEMO", "SYNTHETIC"];
var EXPOSITIONSGRADE = [
  "VERDECKT",
  "NEBENBEREICH",
  "SICHTBEREICH",
  "REPRAESENTATIV"
];
var ERWARTUNGSKLASSEN = ["EINFACH", "MITTEL", "GEHOBEN", "LUXUS"];
var TARGET_SOURCES = [
  "VERTRAG",
  "BAUBESCHRIEB",
  "PLAN",
  "MUSTER",
  "ZUGESICHERTE_EIGENSCHAFT",
  "NORMVERWEIS"
];
var REMEDIATION_STATUSES = [
  "VERLANGT",
  "ANGEBOTEN",
  "VERSUCHT",
  "VERWEIGERT",
  "ERFOLGREICH",
  "TEILWEISE",
  "UNVERHAELTNISMAESSIG_BEHAUPTET"
];
var IMPACT_KINDS = [
  "TECHNISCH",
  "FUNKTIONAL",
  "OPTISCH",
  "NUTZUNGSDAUER",
  "SICHERHEIT"
];
var LICENSE_TYPES = [
  "TRIAL",
  "SINGLE_CASE",
  "PROFESSIONAL",
  "TEAM",
  "REVIEWER",
  "ENTERPRISE"
];
var MWST_STATUSES = ["INKLUSIVE", "EXKLUSIVE", "UNKLAR"];
var CASE_STATUSES = ["OFFEN", "IN_BEARBEITUNG", "ABGESCHLOSSEN", "ARCHIVIERT"];
var ASSESSMENT_STRANDS = ["TECHNISCH", "OPTISCH", "GESAMT"];
var RULE_IDS = {
  DOPPELZAEHLUNG_MINDERUNG_SCHADENERSATZ: "DZ-116-II-305"
};
var WORKFLOW_STEPS = [
  "FALL",
  "VERTRAEGE",
  "SOLL",
  "IST",
  "AUSWIRKUNGEN",
  "NACHBESSERUNG",
  "BEWERTUNGSAUFTRAG",
  "BERECHNUNG",
  "SENSITIVITAET",
  "ABSCHLUSS"
];

// src/evidence.ts
var filled = (v) => typeof v === "string" && v.trim().length > 0;
function rowComplete(r) {
  return filled(r.faktor) && filled(r.feststellung) && filled(r.ausgangswert) && filled(r.vergleichsdaten) && filled(r.unterschiede) && filled(r.zuAbschlaege) && filled(r.beleg) && filled(r.belegqualitaet) && filled(r.begruendung) && filled(r.abschlag) && filled(r.gewicht);
}
function levelAllowsFinal(level) {
  return level === "A" || level === "B" || level === "C" || level === "D";
}
function evidenceGaps(q) {
  const gaps = [];
  const level = q.evidenceLevel;
  const rows = q.matrix ?? [];
  if (level === "E") return ["evidence.gap.levelE"];
  if (level === "A" || level === "B" || level === "C") {
    const minRows = level === "A" ? 2 : 1;
    if (rows.length < minRows) {
      gaps.push(minRows > 1 ? "evidence.gap.matrixCases" : "evidence.gap.matrix");
    } else if (!rows.every(rowComplete)) {
      gaps.push("evidence.gap.matrixRows");
    }
    if (!filled(q.overlapNote)) gaps.push("evidence.gap.overlap");
    if (!q.valuationDate) gaps.push("evidence.gap.stichtag");
    if (!filled(q.stateDefinition)) gaps.push("evidence.gap.zustand");
    if (!filled(q.uncertainty)) gaps.push("evidence.gap.uncertainty");
    if (!filled(q.assessorQualification)) gaps.push("evidence.gap.qualifikation");
  }
  if (level === "A" || level === "B") {
    if (!filled(q.sourceRef)) gaps.push("evidence.gap.fundstelle");
    if (!filled(q.vSoll)) gaps.push("evidence.gap.vSoll");
  }
  if (level === "A" && !filled(q.vIst)) gaps.push("evidence.gap.vIst");
  if (level === "D" && !q.linearTransferOptIn) gaps.push("evidence.gap.dOptIn");
  const rs = q.remediationScenario ?? null;
  const claimsRest = rows.some((r) => /restmangel/i.test(r.faktor ?? "")) || rs !== null && Object.values(rs).some(filled);
  if (claimsRest) {
    if (!rs || ![rs.massnahme, rs.kosten, rs.ergebnis, rs.restmangel, rs.zustand].every(filled)) {
      gaps.push("evidence.gap.remediation");
    }
  }
  if (rs?.matrix && rs.matrix.length > 0 && !rs.matrix.every(rowComplete)) {
    gaps.push("evidence.gap.remediationMatrixRows");
  }
  return gaps;
}

// src/format.ts
var DEC_RE = /^[+-]?\d+(\.\d+)?$/;
function isDecimalString(v) {
  return DEC_RE.test(v.trim());
}
function splitDecimal(v) {
  const t = v.trim();
  const sign = t.startsWith("-") ? "-" : "";
  const unsigned = t.replace(/^[+-]/, "");
  const [int = "0", frac = ""] = unsigned.split(".");
  return { sign, int: int.replace(/^0+(?=\d)/, ""), frac };
}
function groupSwiss(int) {
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
}
function formatAmount(v, decimals = 2) {
  if (!isDecimalString(v)) return v;
  const { sign, int, frac } = splitDecimal(v);
  const scale = BigInt(10) ** BigInt(decimals);
  const fracPadded = (frac + "0".repeat(decimals + 1)).slice(0, decimals + 1);
  const base = BigInt(int) * scale + BigInt(fracPadded.slice(0, decimals) || "0");
  const roundUp = Number(fracPadded[decimals]) >= 5;
  const total = base + (roundUp ? BigInt(1) : BigInt(0));
  const totalStr = total.toString().padStart(decimals + 1, "0");
  const intPart = totalStr.slice(0, totalStr.length - decimals) || "0";
  const fracPart = decimals > 0 ? "." + totalStr.slice(totalStr.length - decimals) : "";
  return `${sign}${groupSwiss(intPart)}${fracPart}`;
}
function formatCHF(v) {
  return `CHF ${formatAmount(v, 2)}`;
}
function roundDisplayCHF(v, step) {
  if (!isDecimalString(v)) return v;
  const { sign, int, frac } = splitDecimal(v);
  const rappen = BigInt(int) * BigInt(100) + BigInt((frac + "00").slice(0, 2));
  const stepRp = BigInt(step) * BigInt(100);
  const half = stepRp / BigInt(2);
  const rounded = (rappen + half) / stepRp * stepRp;
  const chf = rounded / BigInt(100);
  return `${sign}${groupSwiss(chf.toString())}`;
}
function parseDecimalInput(raw) {
  let t = raw.trim().replace(/\s|'|’|CHF/gi, "");
  if (t === "") return null;
  const lastDot = t.lastIndexOf(".");
  const lastComma = t.lastIndexOf(",");
  if (lastDot >= 0 && lastComma >= 0) {
    if (lastComma > lastDot) {
      t = t.replace(/\./g, "").replace(",", ".");
    } else {
      t = t.replace(/,/g, "");
    }
  } else if (lastComma >= 0) {
    if ((t.match(/,/g) || []).length > 1) return null;
    t = t.replace(",", ".");
  }
  if (!DEC_RE.test(t)) return null;
  const { sign, int, frac } = splitDecimal(t);
  return `${sign}${int}${frac ? "." + frac : ""}`;
}
var ZURICH_DATE = new Intl.DateTimeFormat("de-CH", {
  timeZone: "Europe/Zurich",
  day: "2-digit",
  month: "2-digit",
  year: "numeric"
});
var ZURICH_DATETIME = new Intl.DateTimeFormat("de-CH", {
  timeZone: "Europe/Zurich",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit"
});
function formatDate(iso) {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return String(iso);
  return ZURICH_DATE.format(d);
}
function formatDateTime(iso) {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return String(iso);
  return ZURICH_DATETIME.format(d).replace(/[\u{202f}\u{00a0}]/gu, " ");
}
function formatQuotaPercent(v, decimals = 2) {
  if (!isDecimalString(v)) return v;
  const { sign, int, frac } = splitDecimal(v);
  const paddedFrac = frac.padEnd(2, "0");
  const shiftedInt = (int + paddedFrac.slice(0, 2)).replace(/^0+(?=\d)/, "");
  const shiftedFrac = paddedFrac.slice(2);
  const shifted = shiftedFrac ? `${shiftedInt}.${shiftedFrac}` : shiftedInt;
  return `${sign}${formatAmount(shifted, decimals)} %`;
}

// src/i18n.ts
function createTranslator(dict) {
  return {
    t(key, params) {
      let text = dict[key];
      if (text === void 0) {
        return key;
      }
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          text = text.split(`{${k}}`).join(String(v));
        }
      }
      return text;
    },
    has(key) {
      return dict[key] !== void 0;
    }
  };
}

// src/patchnotes.ts
var PATCHNOTES_ARTEN = ["neu", "verbessert", "sicherheit", "behoben", "betrieb"];
var PATCHNOTES = [
  {
    version: "0.11.0",
    datum: "2026-09-23",
    vorher: "0.10.0",
    kurz: "patchnotes.v0_11_0.kurz",
    abschnitte: [
      { art: "verbessert", punkte: ["patchnotes.v0_11_0.verbessert.1", "patchnotes.v0_11_0.verbessert.2"] },
      { art: "sicherheit", punkte: ["patchnotes.v0_11_0.sicherheit.1"] },
      {
        art: "betrieb",
        punkte: ["patchnotes.v0_11_0.betrieb.1", "patchnotes.v0_11_0.betrieb.2", "patchnotes.v0_11_0.betrieb.3"]
      }
    ]
  },
  {
    version: "0.10.0",
    datum: "2026-09-23",
    vorher: "0.9.0",
    kurz: "patchnotes.v0_10_0.kurz",
    abschnitte: [
      { art: "neu", punkte: ["patchnotes.v0_10_0.neu.1"] },
      {
        art: "verbessert",
        punkte: [
          "patchnotes.v0_10_0.verbessert.1",
          "patchnotes.v0_10_0.verbessert.2",
          "patchnotes.v0_10_0.verbessert.3",
          "patchnotes.v0_10_0.verbessert.4"
        ]
      },
      {
        art: "sicherheit",
        punkte: [
          "patchnotes.v0_10_0.sicherheit.1",
          "patchnotes.v0_10_0.sicherheit.2",
          "patchnotes.v0_10_0.sicherheit.3",
          "patchnotes.v0_10_0.sicherheit.4",
          "patchnotes.v0_10_0.sicherheit.5"
        ]
      },
      {
        art: "behoben",
        punkte: [
          "patchnotes.v0_10_0.behoben.1",
          "patchnotes.v0_10_0.behoben.2",
          "patchnotes.v0_10_0.behoben.3",
          "patchnotes.v0_10_0.behoben.4",
          "patchnotes.v0_10_0.behoben.5"
        ]
      },
      {
        art: "betrieb",
        punkte: [
          "patchnotes.v0_10_0.betrieb.1",
          "patchnotes.v0_10_0.betrieb.2",
          "patchnotes.v0_10_0.betrieb.3",
          "patchnotes.v0_10_0.betrieb.4",
          "patchnotes.v0_10_0.betrieb.5"
        ]
      }
    ]
  }
];

// src/schemas.ts
import { z } from "zod";
var zDecimalString = z.string().trim().regex(/^[+-]?\d+(\.\d+)?$/, "Keine g\xFCltige Dezimalzahl");
var zMoney = zDecimalString;
var zQuota = zDecimalString;
var zDateInput = z.string().trim().regex(/^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:\d{2})?)?$/, "Ung\xFCltiges Datum");
var zId = z.string().uuid();
var zText = z.string().trim().min(1).max(1e4);
var zTextOpt = z.string().trim().max(1e4).optional();
var zLabel = z.string().trim().min(1).max(300);
var SetupSchema = z.object({
  organizationName: zLabel,
  adminEmail: z.string().trim().toLowerCase().email(),
  adminDisplayName: zLabel,
  adminPassword: z.string().min(12).max(200)
});
var LoginSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1).max(200)
});
var MfaVerifySchema = z.object({
  code: z.string().trim().regex(/^\d{6}$/, "6-stelliger Code")
});
var UserCreateSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  displayName: zLabel,
  role: z.enum(ROLES),
  password: z.string().min(12).max(200)
});
var UserUpdateSchema = z.object({
  displayName: zLabel.optional(),
  role: z.enum(ROLES).optional()
});
var MfaResetSchema = z.object({
  begruendung: z.string().trim().min(10).max(500)
});
var ResetCompleteSchema = z.object({
  token: z.string().min(10).max(500),
  newPassword: z.string().min(12).max(200)
});
var PasswordChangeSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: z.string().min(12).max(200)
});
var CaseCreateSchema = z.object({
  caseNumber: z.string().trim().min(1).max(60),
  title: zLabel,
  clientName: zTextOpt,
  mandate: zTextOpt,
  purpose: zTextOpt,
  confidentiality: zTextOpt,
  valuationDate: zDateInput.optional()
});
var CaseUpdateSchema = CaseCreateSchema.partial().extend({
  status: z.enum(["OFFEN", "IN_BEARBEITUNG", "ABGESCHLOSSEN", "ARCHIVIERT"]).optional()
});
var PartySchema = z.object({
  role: z.enum(["AUFTRAGGEBER", "UNTERNEHMER", "PLANER", "VERKAEUFER", "KAEUFER", "SONSTIGE"]),
  name: zLabel,
  address: zTextOpt,
  contact: zTextOpt,
  notes: zTextOpt
});
var ContractRelationSchema = z.object({
  label: zLabel,
  contractType: z.enum(CONTRACT_TYPES),
  contractDate: zDateInput.optional(),
  sia118: z.enum(SIA118_OPTIONS).default("UNKLAR"),
  sia118Edition: zTextOpt,
  sia118Deviations: zTextOpt,
  partyAId: zId.optional(),
  partyBId: zId.optional(),
  acceptanceDate: zDateInput.optional(),
  notes: zTextOpt
});
var ContractSchema = z.object({
  contractRelationId: zId.optional(),
  title: zLabel,
  reference: zTextOpt,
  isAddendum: z.boolean().default(false),
  notes: zTextOpt
});
var BuildingObjectSchema = z.object({
  name: zLabel,
  address: zTextOpt,
  objectType: zTextOpt,
  constructionYear: z.number().int().min(1400).max(2200).optional(),
  /** Bewertungskontext (D-053) — belegpflichtig, erzeugt nie einen Wert */
  erwartungsklasse: z.enum(ERWARTUNGSKLASSEN).optional(),
  erwartungsklasseBeleg: zTextOpt,
  notes: zTextOpt
});
var AreaSchema = z.object({
  buildingObjectId: zId.optional(),
  name: zLabel,
  notes: zTextOpt
});
var BuildingElementSchema = z.object({
  buildingObjectId: zId.optional(),
  areaId: zId.optional(),
  name: zLabel,
  ebkphCode: zTextOpt,
  /** Bewertungskontext (D-053) — belegpflichtig, erzeugt nie einen Wert */
  expositionsgrad: z.enum(EXPOSITIONSGRADE).optional(),
  expositionsgradBeleg: zTextOpt,
  notes: zTextOpt
});
var TargetRequirementSchema = z.object({
  buildingElementId: zId.optional(),
  contractRelationId: zId.optional(),
  description: zText,
  source: z.enum(TARGET_SOURCES),
  sourceDetail: zTextOpt,
  normNumber: zTextOpt,
  normEdition: zTextOpt
});
var DefectFindingSchema = z.object({
  buildingElementId: zId.optional(),
  targetRequirementId: zId.optional(),
  contractRelationId: zId.optional(),
  title: zLabel,
  descriptionActual: zTextOpt,
  location: zTextOpt,
  extent: zTextOpt,
  safetyRelevant: z.boolean().default(false),
  structuralSafetyRelevant: z.boolean().default(false),
  durabilityRelevant: z.boolean().default(false),
  discoveryDate: zDateInput.optional(),
  noticeDate: zDateInput.optional(),
  noticeRecipient: zTextOpt,
  noticeContent: zTextOpt
});
var ImpactSchema = z.object({
  defectFindingId: zId,
  kind: z.enum(IMPACT_KINDS),
  description: zText
});
var MeasurementSchema = z.object({
  defectFindingId: zId.optional(),
  name: zLabel,
  value: zDecimalString,
  unit: z.string().trim().min(1).max(30),
  method: zTextOpt,
  device: zTextOpt,
  uncertainty: zTextOpt,
  measuredAt: zDateInput.optional()
});
var RemediationEventSchema = z.object({
  defectFindingId: zId.optional(),
  contractRelationId: zId.optional(),
  status: z.enum(REMEDIATION_STATUSES),
  eventDate: zDateInput.optional(),
  description: zTextOpt
});
var ClaimScenarioSchema = z.object({
  type: z.enum(CLAIM_TYPES),
  status: z.enum(CLAIM_STATUSES),
  title: zLabel,
  strand: z.enum(ASSESSMENT_STRANDS).optional(),
  scenarioRelation: z.enum(SCENARIO_RELATIONS).default("UNGEKLAERT"),
  notes: zTextOpt,
  defectFindingIds: z.array(zId).default([]),
  propertySold: z.boolean().optional(),
  realizedLossDocumented: z.boolean().optional(),
  amountManual: zMoney.optional(),
  amountSource: zTextOpt,
  repairCostComponent: zMoney.optional()
}).superRefine((val, ctx) => {
  if (val.type === "MERKANTILER_MINDERWERT") {
    if (val.propertySold === void 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["propertySold"],
        message: "Pflichtfeld: Wurde die Immobilie verkauft?"
      });
    }
    if (val.realizedLossDocumented === void 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["realizedLossDocumented"],
        message: "Pflichtfeld: Ist eine konkrete Verm\xF6genseinbusse belegt?"
      });
    }
  }
});
var ScenarioMergeSchema = z.object({
  scenarioIds: z.array(zId).min(2),
  justification: zText,
  /** Je Szenario-Paar: betrifft dieselbe Wertkomponente? */
  overlapMatrix: z.array(
    z.object({
      scenarioAId: zId,
      scenarioBId: zId,
      sameValueComponent: z.boolean(),
      comment: zTextOpt
    })
  ),
  amountCombined: zMoney
});
var ValuationModelSchema = z.object({
  claimScenarioId: zId.optional(),
  method: z.literal("RELATIVE_MINDERUNGSMETHODE").default("RELATIVE_MINDERUNGSMETHODE"),
  referenceObject: zTextOpt,
  priceRelevant: zMoney.optional(),
  priceDerivation: zTextOpt,
  valuationDate: zDateInput.optional(),
  mwstStatus: z.enum(MWST_STATUSES).default("UNKLAR"),
  repairCost: zMoney.optional(),
  repairCostSource: zTextOpt,
  residualValueClaimedPositive: z.boolean().default(false),
  notes: zTextOpt
});
var AssessmentCreateSchema = z.object({
  valuationModelId: zId.optional(),
  defectFindingId: zId.optional(),
  strand: z.enum(ASSESSMENT_STRANDS).default("GESAMT"),
  templateVersionId: zId
});
var AssessmentNodesSaveSchema = z.object({
  nodes: z.array(
    z.object({
      nodeKey: z.string().trim().min(1).max(200),
      weightPercent: zQuota,
      fulfillment: zQuota.optional(),
      /** Blatt ausdrücklich «nicht bewertet» (kein Erfüllungsgrad, mindert die Deckung) */
      notAssessed: z.boolean().optional(),
      /** Erfüllungsgrad beruht auf gutachterlicher Annahme (zählt zum Annahmeanteil) */
      assumptionBased: z.boolean().optional(),
      justification: zTextOpt
    })
  )
});
var AssessmentCompleteSchema = z.object({
  /** Sicherheitsregel 3.5: aktive Bestätigung, falls sicherheitsrelevante Mängel betroffen sind */
  safetyConfirmation: z.boolean().optional()
});
var MatrixRowSchema = z.object({
  faktor: z.string().trim().min(1).max(200),
  feststellung: zTextOpt,
  ausgangswert: zTextOpt,
  vergleichsdaten: zTextOpt,
  unterschiede: zTextOpt,
  zuAbschlaege: zTextOpt,
  /** Resultierender Faktorabschlag aus dieser Perspektive, Quote [0,1] */
  abschlag: z.string().trim().min(1),
  beleg: zTextOpt,
  /** Modellgewicht = fachliche Relevanz, in Prozent; Summe 100 */
  gewicht: z.string().trim().min(1),
  belegqualitaet: zTextOpt,
  unsicherheitswirkung: zTextOpt,
  begruendung: zTextOpt,
  /**
   * K4 Option a (D-031): Unter-/Oberwert des Faktorabschlags für die rechnerische
   * Bandbreite. All-or-none über alle Zeilen einer Matrix (Prüfung im superRefine
   * und fail-closed im Rechenkern).
   */
  abschlagMin: z.string().trim().min(1).optional(),
  abschlagMax: z.string().trim().min(1).optional(),
  /**
   * Übernommener Referenzfall (D-035, Review-Punkt 8): Herkunft der Zeile. refDataClass
   * DEMO/SYNTHETIC blockiert den realen Finalbericht (Provenienz ≠ Beweiswert).
   */
  refId: z.string().uuid().optional(),
  refDataClass: z.enum(DATENKLASSEN).optional()
});
function refineMatrixBand(rows, path, ctx) {
  if (!rows?.length) return;
  const withAny = rows.filter((r) => r.abschlagMin !== void 0 || r.abschlagMax !== void 0);
  if (withAny.length === 0) return;
  const complete = rows.every((r) => r.abschlagMin !== void 0 && r.abschlagMax !== void 0);
  if (!complete) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path,
      message: "Bandbreite: entweder tragen alle Matrixzeilen Unter- und Oberwert oder keine (keine teilweisen Angaben)."
    });
  }
}
var QDeterminationSchema = z.object({
  valuationModelId: zId,
  label: zLabel,
  evidenceLevel: z.enum(EVIDENCE_LEVELS),
  value: zQuota.optional(),
  vIst: zMoney.optional(),
  vSoll: zMoney.optional(),
  linearTransferOptIn: z.boolean().default(false),
  justification: zText,
  sourceRef: zTextOpt,
  calibrationRef: zTextOpt,
  // V15a (D-025): Bewertungsmatrix + Pflichtfelder für die Evidenzstufen A–C.
  derivationMatrix: z.array(MatrixRowSchema).optional(),
  overlapNote: zTextOpt,
  valuationDate: z.string().optional(),
  stateDefinition: zTextOpt,
  uncertainty: zTextOpt,
  assessorQualification: zTextOpt,
  remediationScenario: z.object({
    massnahme: zTextOpt,
    kosten: zTextOpt,
    ergebnis: zTextOpt,
    restmangel: zTextOpt,
    zustand: zTextOpt,
    vRest: zMoney.optional(),
    // K2 (D-031): gesonderte Restmangel-Matrix; vRest/qRest werden serverseitig
    // berechnet (schreibgeschützt), eingehende Werte ohne Matrix bleiben
    // unfreigegebene Szenarioschätzung.
    matrix: z.array(MatrixRowSchema).optional()
  }).optional()
}).superRefine((val, ctx) => {
  refineMatrixBand(val.derivationMatrix, ["derivationMatrix"], ctx);
  refineMatrixBand(val.remediationScenario?.matrix, ["remediationScenario", "matrix"], ctx);
  const hasValue = val.value !== void 0;
  const hasRatio = val.vIst !== void 0 && val.vSoll !== void 0;
  switch (val.evidenceLevel) {
    case "A":
    case "B":
      if (!val.sourceRef) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["sourceRef"],
          message: "Quellenangabe ist f\xFCr Evidenzstufe A/B Pflicht."
        });
      }
      if (!hasValue && !hasRatio && !val.derivationMatrix?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["value"],
          message: "q-Wert, V_ist/V_soll oder Bewertungsmatrix erfassen."
        });
      }
      break;
    case "C":
      if (!val.calibrationRef && !val.derivationMatrix?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["calibrationRef"],
          message: "Kalibrierungsnachweis oder Bewertungsmatrix ist f\xFCr Evidenzstufe C Pflicht."
        });
      }
      if (!hasValue && !hasRatio && !val.derivationMatrix?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["value"],
          message: "q-Wert, V_ist/V_soll oder Bewertungsmatrix erfassen."
        });
      }
      break;
    case "D":
      if (!val.linearTransferOptIn) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["linearTransferOptIn"],
          message: "Evidenzstufe D verlangt das ausdr\xFCckliche Opt-in zur \xDCbertragungshypothese."
        });
      }
      if (hasValue || val.vIst !== void 0 || val.vSoll !== void 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["value"],
          message: "Bei Evidenzstufe D wird q aus u \xFCbernommen \u2014 kein eigener Wert."
        });
      }
      break;
    case "E":
      if (!hasValue && !hasRatio && !val.derivationMatrix?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["value"],
          message: "q-Wert, V_ist/V_soll oder Bewertungsmatrix erfassen."
        });
      }
      break;
  }
});
var LifespanInputSchema = z.object({
  replacementCost: zMoney,
  replacementCostSource: zText,
  discountRate: zQuota,
  discountRateSource: zText,
  yearsDefective: zDecimalString,
  yearsSound: zDecimalString,
  yearsSource: zText
});
var CalculationRequestSchema = z.object({
  qDeterminationId: zId.optional(),
  assessmentId: zId.optional(),
  lifespan: LifespanInputSchema.optional()
});
var SensitivityParamSchema = z.object({
  kind: z.enum([
    "FULFILLMENT",
    "WEIGHT",
    "Q",
    "PRICE",
    "REPAIR_COST",
    "DISCOUNT_RATE",
    "YEARS_DEFECTIVE",
    "YEARS_SOUND"
  ]),
  nodeId: z.string().optional(),
  label: z.string().max(200).optional(),
  low: zDecimalString,
  high: zDecimalString
});
var SensitivityRequestSchema = z.object({
  params: z.array(SensitivityParamSchema).max(30),
  scenarios: z.object({
    low: z.array(SensitivityParamSchema.omit({ low: true, high: true, label: true }).extend({ value: zDecimalString })),
    mid: z.array(SensitivityParamSchema.omit({ low: true, high: true, label: true }).extend({ value: zDecimalString })),
    high: z.array(SensitivityParamSchema.omit({ low: true, high: true, label: true }).extend({ value: zDecimalString }))
  }).optional(),
  metric: z.enum(["REDUCTION_AMOUNT", "LIFESPAN_DELTA"]).default("REDUCTION_AMOUNT")
});
var SourceSchema = z.object({
  ident: z.string().trim().min(1).max(120),
  type: z.enum(SOURCE_TYPES),
  citation: zText,
  reviewStatus: z.enum(REVIEW_STATUSES).default("AUSSTEHEND"),
  notes: zTextOpt
});
var ReferenzfallSchema = z.object({
  markt: z.enum(MAERKTE),
  templateKey: z.string().trim().min(1).max(120),
  faktor: z.string().trim().min(1).max(200),
  beschreibung: zText,
  /** Ausgangsabschlag als Quote [0,1] */
  abschlag: zQuota,
  abschlagMin: zQuota.optional(),
  abschlagMax: zQuota.optional(),
  anpassungsregel: zTextOpt,
  /** Bewertungskontext des Vergleichsfalls (D-053) — Vergleichsdimension, kein Rechenwert */
  expositionsgrad: z.enum(EXPOSITIONSGRADE).optional(),
  erwartungsklasse: z.enum(ERWARTUNGSKLASSEN).optional(),
  /** Belegte Herkunft ist Pflicht — keine fingierten Referenzwerte (Sperre 15.5) */
  herkunft: zText,
  /** Methoden- und Zahlen-Provenienz getrennt (D-035, Review-Punkt 7). */
  methodSource: zTextOpt,
  numericSource: zTextOpt,
  /** Datenklasse: nur reale/öffentliche Fälle über die API; DEMO/SYNTHETIC bleiben dem Seed vorbehalten. */
  dataClass: z.enum(["REAL_CURATED", "PUBLIC_DECISION"]).default("REAL_CURATED"),
  /** Verwertbarkeitsklasse J1–J5 (nur bei Gerichts-/Entscheidungsdaten). */
  verwertbarkeit: zTextOpt,
  anonymisiert: z.boolean().default(false),
  isDemo: z.boolean().default(false),
  notes: zTextOpt
}).superRefine((val, ctx) => {
  const hasMin = val.abschlagMin !== void 0;
  const hasMax = val.abschlagMax !== void 0;
  if (hasMin !== hasMax) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Bandbreite: entweder beide Grenzen (Unter- und Oberwert) oder keine.",
      path: [hasMin ? "abschlagMax" : "abschlagMin"]
    });
  }
});
var ReferenzfallFreigabeSchema = z.object({
  /**
   * Historisches Feld des mit D-082 entfernten Publikationswegs.
   *
   * Der globale Kern-Pool ist systemverwaltet; über Mandantenrouten ist er ausschliesslich
   * lesbar. Das Feld bleibt im Schema, damit eine ältere oder manipulierte Anfrage einen
   * benannten `403 GLOBAL_POOL_SYSTEM_MANAGED` erhält statt eines Validierungsfehlers. Ein
   * späterer, systemverwalteter Moderationsprozess bringt seinen eigenen Vertrag mit.
   */
  inKernPool: z.boolean().default(false),
  anonymisiert: z.literal(true, {
    errorMap: () => ({ message: "Freigabe verlangt die best\xE4tigte Anonymisierung." })
  })
});
var ReportFinalizeSchema = z.object({
  /** Freigabe ist personengebunden; Anzeige-Rundung wählbar (Auftrag 5.5) */
  displayRoundCHF: z.union([z.literal(100), z.literal(1e3)]).default(100),
  remarks: zTextOpt
});
export {
  ASSESSMENT_STRANDS,
  AreaSchema,
  AssessmentCompleteSchema,
  AssessmentCreateSchema,
  AssessmentNodesSaveSchema,
  BuildingElementSchema,
  BuildingObjectSchema,
  CASE_STATUSES,
  CLAIM_STATUSES,
  CLAIM_TYPES,
  CONTRACT_TYPES,
  CalculationRequestSchema,
  CaseCreateSchema,
  CaseUpdateSchema,
  ClaimScenarioSchema,
  ContractRelationSchema,
  ContractSchema,
  DATENKLASSEN,
  DefectFindingSchema,
  ERWARTUNGSKLASSEN,
  EVIDENCE_LEVELS,
  EXPOSITIONSGRADE,
  IMPACT_KINDS,
  ImpactSchema,
  LEGAL_REGIMES,
  LICENSE_TYPES,
  LifespanInputSchema,
  LoginSchema,
  MAERKTE,
  MWST_STATUSES,
  MatrixRowSchema,
  MeasurementSchema,
  MfaResetSchema,
  MfaVerifySchema,
  NICHT_EVIDENZ_KLASSEN,
  PATCHNOTES,
  PATCHNOTES_ARTEN,
  PartySchema,
  PasswordChangeSchema,
  QDeterminationSchema,
  REGIME_CUTOFF_ISO,
  REMEDIATION_STATUSES,
  REVIEW_STATUSES,
  ROLES,
  RULE_IDS,
  ReferenzfallFreigabeSchema,
  ReferenzfallSchema,
  RemediationEventSchema,
  ReportFinalizeSchema,
  ResetCompleteSchema,
  SCENARIO_RELATIONS,
  SIA118_OPTIONS,
  SOURCE_TYPES,
  ScenarioMergeSchema,
  SensitivityParamSchema,
  SensitivityRequestSchema,
  SetupSchema,
  SourceSchema,
  TARGET_SOURCES,
  TargetRequirementSchema,
  UserCreateSchema,
  UserUpdateSchema,
  ValuationModelSchema,
  WORKFLOW_STEPS,
  createTranslator,
  emailDomainOf,
  emailMatchesCompanyDomain,
  evidenceGaps,
  formatAmount,
  formatCHF,
  formatDate,
  formatDateTime,
  formatQuotaPercent,
  isBlockedEmailDomain,
  isCompanyEmail,
  isDecimalString,
  levelAllowsFinal,
  parseDecimalInput,
  roundDisplayCHF,
  zDateInput,
  zDecimalString,
  zMoney,
  zQuota
};
//# sourceMappingURL=index.mjs.map